import { InsertionPointService } from './insertion-point.service';
import { NotesFilterService } from '../utilites/notes-filter.service';
import { NotebooksFirestoreService } from './notebooks-firestore.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject, Subscription } from 'rxjs';
import { addDoc, collection, doc, Firestore, getDocs, getFirestore, limit, orderBy, query, updateDoc } from 'firebase/firestore';
import { collectionData } from '../../utils/custom-firestore.utils';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import {  Note, Notebook, TodoItemNode } from '../../model/notes.model';
import { NotesFirestoreService } from './notes-firestore.service';
import { SettingsService } from './settings.service';
import { OrderBy } from 'src/app/model/settings.model';
import { AuthService } from '../authentication/auth.service';

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable({
  providedIn: 'root'
})
export class LocalDatabaseService {

  notebookObservable: Observable<Notebook[]>;
  sub: Subscription;

  dataChange = new BehaviorSubject<TodoItemNode[]>([]);

  selectedInsertionPointChange = new Subject<string>();

  insertionPointString: string;

  selectedNote: TodoItemNode;

  exportFromNode: TodoItemNode;

  filterOutNotebooks: TodoItemNode[];

  currentPage: string;
  notebookIdToFilter: string;
  noteIdToFilter: string;

  filterOutNotSelected:boolean = false;

  reviewNote: TodoItemNode;

  private firestore: Firestore = getFirestore();
  private storage = getStorage();

  constructor(
      private notebooksFirestore: NotebooksFirestoreService, private notesFirestore: NotesFirestoreService,
      private notesFilter: NotesFilterService, private insertionPoint: InsertionPointService,
      private settingsServices: SettingsService, private authService: AuthService) {

    this.notebooksFirestore.setLocalDb(this);
    this.notesFirestore.setLocalDb(this);
    this.notesFilter.setLocalDb(this);
    this.insertionPoint.setLocalDb(this);

    this.filterOutNotebooks = new Array<TodoItemNode>();
    this.initialize();

  }

  /**
   * Initializes the ChecklistDatabaseService.
   * It initializes the dataChange BehaviorSubject with an empty array.
   * It starts the insertion point listener and notebooks listener.
   */
  initialize() {

    let add_data = new Array<TodoItemNode>();

    this.dataChange.next(add_data);

    // Start listening for insertion point changes
    this.insertionPoint.startInsertionPointListener();

    // Start listening for notebooks changes
    this.notebooksFirestore.startNotebooksListener();
  }


  /**
   * Sets the current page and optionally filters by notebook and note ids.
   *
   * @param page The name of the page to set as the current page.
   * @param notebookId The id of the notebook to filter by, optional.
   * @param noteId The id of the note to filter by, optional.
   */
  setCurrentPage(page: string, notebookId?: string, noteId?: string) {
    this.currentPage = page;
    this.notebookIdToFilter = notebookId ?? null;
    this.noteIdToFilter = noteId ?? null;

    if ((this.currentPage == 'notebook') && (this.notebookIdToFilter != null)) {
      this.notesFilter.filterOutNotebooksById(this.notebookIdToFilter);
    }

    console.log(`setCurrentPage = ${this.currentPage} notebookIdToFilter = ${this.notebookIdToFilter} noteIdToFilter = ${this.noteIdToFilter}`);
  }

  get data(): TodoItemNode[] {
    return this.dataChange.value;
  }

  resetNotebooksNavigation () {
    this.data.forEach(notebookItem => {
      notebookItem.currentExpandedNode = notebookItem;
      notebookItem.expandadedStack = new Array<TodoItemNode>();
    })
  }

  setReviewNote(note: TodoItemNode) {
    this.reviewNote = note;
  }

  getReviewNote() {
    return this.reviewNote;
  }

  getPathFromNote(note: TodoItemNode) {

    let path = note.item

    let currentNode = note.parentRef;

    while (currentNode) {
      path = currentNode.item + ' > ' + path;
      currentNode = currentNode.parentRef;
    }

    return path;
  }

  getInsertPointLocation() {
    return this.insertionPoint.insertionPointLocation;
  }

  private findParent(possibleParent: TodoItemNode, id:string): TodoItemNode {

    if (possibleParent.id === id)
      return possibleParent;

    else if (possibleParent.children) {

      for (let p of possibleParent.children) {
        const found = this.findParent(p, id);
        if (found)
          return found;
      }
    }
    return null;
  }

  private findMe(possibleMe: TodoItemNode, id: string): TodoItemNode {

    if (possibleMe.id === id)
      return possibleMe;

    else if (possibleMe.children) {

      for (let p of possibleMe.children) {
        const found = this.findMe(p, id);
        if (found)
          return found;
      }
    }

    return null;
  }


  /** Methods call from listener ACTIONS from firestore */

  gatherAllNotesFromNotebook(notebookItem: TodoItemNode, notes: Note[]) {

    let parentIdToCheck = notebookItem.id;

    // usa filters: https://www.w3schools.com/jsref/jsref_filter.asp

    function checkParentID(note: Note) {

      //console.log(parentIdToCheck + ' == ' + note.parent_id);
      return (parentIdToCheck == note.parent_id);
    }

    function getAllChildren(todoItemNode: TodoItemNode) {

      //console.log("Nodo: " + todoItemNode.item);
      parentIdToCheck = todoItemNode.id;
      let myChildren: Note[] = notes.filter(checkParentID); // ver de passar o id por parametro

      //console.log("Meus fihos são: ");
      //console.log(myChildren);

      if (myChildren && (myChildren.length > 0)) {

        //only if it is the first time here
        if (!todoItemNode.children)
          todoItemNode.children = new Array<TodoItemNode>();

        todoItemNode.has_child = true;

        myChildren.forEach(childNote => {

          //If already exist continue to next element  ---
          //tem que atualizar ele dai ao invés de nao fazer nada - se da update por acaso
          if (!(todoItemNode.children.findIndex(itemNode => { return itemNode.id == childNote.id }) > -1)) {

            let newChild = new TodoItemNode();
            newChild.id = childNote.id;
            newChild.item = childNote.text;
            newChild.parent_id = todoItemNode.id;
            newChild.notebook_id = todoItemNode.notebook_id;
            newChild.children = null;
            newChild.parentRef = todoItemNode;
            newChild.priority = childNote.priority;
            newChild.image_path = childNote.image_path ? childNote.image_path : null;
            newChild.imageDownloadUrl = null;
            newChild.file = null;
            newChild.maxChildrenPriorityNumber = 0;
            newChild.has_child = false;
            newChild.notebookRef = notebookItem;

            newChild.createdAt = childNote.createdAt ?? new Date(0);

            // Adiciona de acordo com ordem de prioridade - primeiro adiciona depois classifica
            // Atualiza a prioridade maxima do pai se passou
            todoItemNode.children.push(newChild);

            if (newChild.priority > todoItemNode.maxChildrenPriorityNumber)
              todoItemNode.maxChildrenPriorityNumber = newChild.priority;

            getAllChildren(newChild);

          } else {

            getAllChildren(todoItemNode.children.find(itemNode => { return itemNode.id == childNote.id }));

          }

        });

        todoItemNode.children.sort((a, b) => (a.priority - b.priority));
        // Classifica só aqui depois por prioridade
      }
    }

    getAllChildren(notebookItem);

    this.dataChange.next(this.data);
  }

  noteAddAction(notebookItem: TodoItemNode, note: Note) {


    //console.log("noteAddAction - note = ", note);

    // tratar caso de has_child caso for notebook o note sem filhos

    // acha o parent dentro do caderno
    const myParent = this.findParent(notebookItem, note.parent_id);

    if (!myParent) {
      console.log("Error - could not find my dad");
      return;
    }

    // se nao tem filhos cria os filhos
    if (!myParent.has_child) {
      myParent.children = (myParent.children) ? myParent.children : new Array();
      myParent.has_child = true;
    }

    let new_note = new TodoItemNode();

    new_note.id = note.id;
    new_note.item = note.text;
    new_note.parent_id = myParent.id;
    new_note.notebook_id = note.notebook_id;
    new_note.children = null;
    new_note.parentRef = myParent;
    new_note.priority = note.priority;
    new_note.image_path = note.image_path ? note.image_path : null;
    new_note.imageDownloadUrl = null;
    new_note.file = null;
    new_note.maxChildrenPriorityNumber = 0;
    new_note.has_child = note.has_child;
    new_note.notebookRef = notebookItem;

    new_note.createdAt = note.createdAt ?? new Date(0);

    //console.log("setou o notebookRef para notebook = " + new_note.notebookRef.item);
    //console.log("current Expandade para nota é = " + new_note.notebookRef.currentExpandedNode.item);
    // Esqueci o id dele mesmo aqui
    /*
    let new_note = {
      item: note.text,
      parent_id: note.parent_id,
      notebook_id: notebookItem.id,
      parentRef: myParent,
      maxChildrenPriorityNumber: 0,
      priority: note.priority
     } as TodoItemNode;*/

     // ordenar de acordo com prioridade
       // acho que nao pq adiciona no final sempre

     myParent.children.push(new_note);


    this.dataChange.next(this.data);
  }

  noteModifiedAction(notebookItem: TodoItemNode, note: Note) {

    //console.log("noteModifiedAction - note = ", note);

    // find and modify
    const me = this.findMe(notebookItem, note.id);

    if (!me) {
      console.log("Error - could not find me");
      return;
    }

    me.item = note.text;
    me.maxChildrenPriorityNumber = note.max_children_priority;


    //checa se mudou image_path e se tem se tem imageDownloadUrl, se tiver image DonwloadUrl tem q refazer o donwnload para a nova imagem
    //se mudou image_path
    if (me.image_path != note.image_path) {
      me.imageDownloadUrl = null;
      me.image_path = note.image_path ? note.image_path : null;

      if (me.image_path) {
        // faz o download da imagem
        const storageRef = ref(this.storage, me.image_path);

        getDownloadURL(storageRef)
          .then((url) => {
            me.imageDownloadUrl = url;
            this.dataChange.next(this.data);
          })
          .catch((error) => {
            console.error('Error downloading image:', error);
          });
      }
    };

    // Same parent - just update priority
    if (me.parent_id === note.parent_id) {

      //console.log("modificou - parent_id igual");

      if (me.priority != note.priority) {
       //console.log("mudou prioridade - caiiu aqui");
        me.priority = note.priority;
        const myParent = this.findMe(notebookItem, me.parent_id);
        if (myParent && myParent.children) {
          myParent.children.sort((a, b) => (a.priority - b.priority));
        }
      }
      this.dataChange.next(this.data);
      return;
    }


    // if parent_id is different - move object from old parent to new one

    // me.parent_id != note.parent_id
    //console.log("modificou - parent_id diferente, me.parent_id = " + me.parent_id + "  note.parent_id = " + note.parent_id);

    const oldParent = me.parentRef; // this.findMe(notebookItem, me.parent_id);
    const newParent = this.findMe(notebookItem, note.parent_id);

    //console.log('oldParent = ' + oldParent + ' old parent childreen = ' + oldParent.children + ' newParent = ' + newParent);

    if (oldParent && oldParent.children && newParent) { // TODO precisa essa checcagem?
      //console.log("mudando pais");

      me.parent_id = note.parent_id;
      me.parentRef = newParent;
      me.priority = note.priority;

      oldParent.children.splice(oldParent.children.findIndex(itemNode => { return itemNode.id == me.id }), 1);


      // checa se era ulitmo filho - se sim, has_child = false
      if (oldParent.children.length <= 0) {
        oldParent.has_child = false;
        oldParent.children = null;

        // checa se era ulitmo filho - se sim, has_child = false
        if (oldParent.id === oldParent.notebook_id) {
          const notebookDocRef = doc(
            this.firestore,
            `users/${this.authService.userId}/notebooks/${oldParent.id}`
          );

          updateDoc(notebookDocRef, {
            has_child: false,
            max_children_priority: 0,
          });
        } else {
          const noteDocRef = doc(
            this.firestore,
            `users/${this.authService.userId}/notebooks/${oldParent.notebook_id}/notes/${oldParent.id}`
          );

          updateDoc(noteDocRef, {
            has_child: false,
            max_children_priority: 0,
          });
        }
      }

      let new_note = new TodoItemNode();

      //se fosse interface ok, mas é classe
      //let new_note = {...me};

      new_note.id = me.id;
      new_note.item = me.item;
      new_note.parent_id = me.parent_id;
      new_note.notebookRef = me.notebookRef
      new_note.notebook_id = me.notebook_id;
      new_note.parentRef = me.parentRef;
      new_note.priority = me.priority;
      new_note.image_path = me.image_path ? me.image_path : null;
      new_note.imageDownloadUrl = null;
      new_note.file = null;
      new_note.maxChildrenPriorityNumber = me.maxChildrenPriorityNumber;
      new_note.has_child = me.has_child;
      new_note.file = me.file;
      new_note.children = me.children;

      // update here
      if (new_note.children && new_note.children.length > 0)
        new_note.children.forEach( n => {n.parentRef = new_note;});

      if (!newParent.children) {
        newParent.children = new Array<TodoItemNode>();
        newParent.has_child = true; // TODO ver se tem q atualizar no db tmb , ver status do db
      }

      newParent.children.push(new_note);
      newParent.children.sort((a, b) => (a.priority - b.priority));
    }

    this.dataChange.next(this.data);

  }

  noteRemovedAction(notebookItem: TodoItemNode, note: Note) {

    // find and remove
    const me = this.findMe(notebookItem, note.id);

    if (!me) {
      console.log("Error - could not find me, probably I was removed before");
      return;
    }

    // delete my children
    this.deleteMyChildrenFromLocalDB(me);

    // delete me now
    const parent_ref = me.parentRef;
    let childrenArray = me.parentRef.children;
    if (childrenArray && (childrenArray.length > 0))
      childrenArray.splice(childrenArray.findIndex(itemNode => { return itemNode.id == me.id }), 1);

    // update parent if has no child anymore
    if (parent_ref && ((!parent_ref.children) || (parent_ref.children.length < 1))) {
      parent_ref.has_child = false;
      parent_ref.children = null;
      // TODO -- aqui tinha q atualizar pai - com has_child = false -- Mas faz dinammicamente no listener do notebook
      // mas vou fazer
      if (me.parentRef.id === me.parentRef.notebook_id) {
        const notebookDocRef = doc(
          this.firestore,
          `users/${this.authService.userId}/notebooks/${me.parentRef.id}`
        );

        updateDoc(notebookDocRef, {
          has_child: false,
          max_children_priority: 0,
        });
      } else {
        const noteDocRef = doc(
          this.firestore,
          `users/${this.authService.userId}/notebooks/${me.parentRef.notebook_id}/notes/${me.parentRef.id}`
        );

        updateDoc(noteDocRef, {
          has_child: false,
          max_children_priority: 0,
        });
      }

    }

    this.dataChange.next(this.data);
  }

  notebookAddAction(notebook: Notebook) {
    // adiciona no local

    //let add_data_2 = this.data;

    let notebookItem = new TodoItemNode();
    notebookItem.item = notebook.text;
    notebookItem.id = notebook.id;
    notebookItem.notebook_id = notebook.id;
    notebookItem.parent_id = '';
    notebookItem.children = null;
    notebookItem.parentRef = null;

    // *** campo 'max_children_priority' foi adicionado depois no notebook - dai tem q testar para ver se nao está undefined
    notebookItem.maxChildrenPriorityNumber = notebook.max_children_priority ? notebook.max_children_priority : 0;

    //notebookItem.filterOut = true;

    notebookItem.has_child = false;
    //https://stackoverflow.com/questions/47997748/is-possible-to-check-if-a-collection-or-sub-collection-exists

    notebookItem.subscribeChildren = null;
    notebookItem.childrenNotesLoaded = false;
    notebookItem.notesListeningStarted = false;

    // For handle navigation on mobile devices
    notebookItem.expandadedStack = new Array<TodoItemNode>();
    notebookItem.currentExpandedNode = notebookItem;
    //console.log("Notebook setou currentExpandedNode = " + notebookItem.item);

    notebookItem.lastChangeTime = notebook.lastChangeTime ?? new Date(0);
    notebookItem.createdAt = notebook.createdAt ?? new Date(0);


    const notesCollectionRef = collection(
      this.firestore,
      `users/${this.authService.userId}/notebooks/${notebookItem.id}/notes`
    );

    // Consulta com limite de 1 documento
    const notesQuery = query(notesCollectionRef, limit(1));

    getDocs(notesQuery).then((querySnapshot) => {
      if (!querySnapshot.empty) {
        notebookItem.has_child = true;
        // console.log('Tem filhos = true');
      } else {
        // console.log('Tem filhos = false');
        (1);
      }
    }).finally(() => {
      if (this.currentPage === 'notebook' && notebookItem.id !== this.notebookIdToFilter) {
        console.log('Adicionando na lista de filterOut', notebookItem.item);
        this.filterOutNotebooks.push(notebookItem);
      } else {
        console.log('Adicionando na lista principal', notebookItem.item);
        this.data.push(notebookItem);
        this.sortNotebooks(); // Sinaliza alteração nos dados
      }
    });

    //console.log("Calling notebookAddAction ", notebook);
  }

  notebookModifiedAction(notebook: Notebook) {
    // modifica no local
    const notebookToChange = this.data[this.data.findIndex(itemNode => {return itemNode.id == notebook.id})];

    const sort = (notebookToChange.item !== notebook.text);

    notebookToChange.item = notebook.text;

    notebookToChange.lastChangeTime = notebook.lastChangeTime?? new Date(0);

    // faz isso por campo has_child foi adicionado depoois - pode estar undefined se pegar do server
    notebookToChange.has_child = notebook.has_child ? true:false;

    notebookToChange.maxChildrenPriorityNumber = notebook.max_children_priority ? notebook.max_children_priority : 0;

    if (sort)
      this.sortNotebooks(); // sinalize data change inside this method
    else
      this.dataChange.next(this.data);
  }

  notebookRemovedAction(notebook: Notebook) {
    // remove from local database
    this.data.splice(this.data.findIndex(itemNode => {return itemNode.id == notebook.id}), 1);
    this.dataChange.next(this.data);
  }

  deleteMyChildrenFromLocalDB(n: TodoItemNode) {
    // ? ver pq quando declado como funcao , nao consigo acessae this.property

   // deleta os filhos no banco todos do index - find index -- ver se da pra fazer um batch

   // acho q da pra usar as outras funcoes do javascript assim
   while (n.children && (n.children.length > 0)) {
     let deleteItem = n.children.pop();
     this.deleteMyChildrenFromLocalDB(deleteItem);
   }
 }

 /** MEthods call from this service and components */

  sortNotebooks(orderBy?: OrderBy) {

    if (!orderBy)
      orderBy = this.settingsServices.mainSettings.orderNotebooksBy ?? OrderBy.ALPHA;

    if (!this.data)
      return;

    /*
    let selectedNotebookAsFirst = (this.currentPage == 'insertion-point');
    let selectedNotebookId  = (this.insertionPoint.insertionPointLocation)  ? this.insertionPoint.insertionPointLocation.notebook_id : 0;
    */

    this.data.sort((a, b) => {

      /*
      if (selectedNotebookAsFirst && (selectedNotebookId === a.id))
        return -1;

      if (selectedNotebookAsFirst && (selectedNotebookId === b.id))
        return 1;
      */

      if (orderBy == OrderBy.ALPHA) {
        let x = (a.item && typeof a.item === 'string') ? a.item.toLowerCase() : '';
        let y = (b.item && typeof b.item === 'string') ? b.item.toLowerCase() : '';

        if (x < y) {return -1;}
        if (x > y) {return 1;}
      }
      else if (orderBy == OrderBy.DATE) {
        let x = a.lastChangeTime ?? new Date(0);
        let y = b.lastChangeTime ?? new Date(0);

        if (x > y) {return -1;}
        if (x < y) {return 1;}
      }

      return 0;
    });

    this.dataChange.next(this.data);
  }

  /*******************************************************/
  // NOT USED: Esse nao vai mais ser usado - da para remover
  /*******************************************************/
  getAllNoteFromNotebook(notebookItem: TodoItemNode) {

    let noteObservable: Observable<Note[]>;
    let subNote: Subscription;
    // ordena por prioridade depois

    // Referência à coleção no Firestore
    const notesCollectionRef = collection(
      this.firestore,
      `users/${this.authService.userId}/notebooks/${notebookItem.id}/notes`
    );

    // Consulta com ordenação (se necessário)
    //const notesQuery = query(notesCollectionRef, orderBy('priority'));

    // ordena por prioridade depois
    const notesQuery = query(notesCollectionRef);

    // Observable dos dados da coleção com conversão explícita
    noteObservable = collectionData(notesQuery, { idField: 'id' }).pipe(
      map((docs) => docs.map((doc) => doc as Note))
    );

    // ver se quando muda só um documento conta só como uma leitura ou varias já que retorna varias coisas aqui.

    // pode acessar variaveis locais dentro de um promisse - código assincrono?
    // - ver pilha, etc -- ele mantem a variavel - nao tira da pilha

    notebookItem.subscribeChildren =  subNote = noteObservable.subscribe(notes => {

     // if(notebookItem.item === "TESTE2")
     //  return;

      //console.log('esses sao as notas');
      //console.log('caderno id = ' + notebookItem.id);
      //console.log(notes);

      //let add_data_2 = this.data;
      let parentIdToCheck = notebookItem.id;

      // Lê todos de uma vez
      // let add_data_notes = this.data;

      // faz isso ao invés
      // ja tem o id do carderno
      // procura por filhos direto do caderno
      // pra cadas filho adicionaco procura por seus filhos e assim por diante se tiver -- etc -- bem mais facil

      // this.dataChange.next(add_data_notes);
      //testar ve se unsub funciona
      //subNote.unsubscribe();


      // usa filters: https://www.w3schools.com/jsref/jsref_filter.asp

      function checkParentID(note: Note) {

        //console.log(parentIdToCheck + ' == ' + note.parent_id);
        return (parentIdToCheck == note.parent_id);
      }

      function getAllChildren(todoItemNode: TodoItemNode) {

        //console.log("Nodo: " + todoItemNode.item);
        parentIdToCheck = todoItemNode.id;
        let myChildren: Note[] = notes.filter(checkParentID); // ver de passar o id por parametro

        //console.log("Meus fihos são: ");
        //console.log(myChildren);

        if (myChildren && (myChildren.length > 0)) {

          //only if it is the first time here
          if (!todoItemNode.children)
            todoItemNode.children = new Array<TodoItemNode>();

          todoItemNode.has_child = true;

          myChildren.forEach(childNote => {

            //If already exist continue to next element  ---
            //tem que atualizar ele dai ao invés de nao fazer nada - se da update por acaso
            if (!(todoItemNode.children.findIndex(itemNode => { return itemNode.id == childNote.id }) > -1)) {

              let newChild = new TodoItemNode();
              newChild.id = childNote.id;
              newChild.item = childNote.text;
              newChild.parent_id = todoItemNode.id;
              newChild.notebook_id = todoItemNode.notebook_id;
              newChild.children = null;
              newChild.parentRef = todoItemNode;
              newChild.priority = childNote.priority;
              newChild.image_path = childNote.image_path ? childNote.image_path : null;
              newChild.file = null;
              newChild.maxChildrenPriorityNumber = 0;
              // Adiciona de acordo com ordem de prioridade - primeiro adiciona depois classifica
              // Atualiza a prioridade maxima do pai se passou
              todoItemNode.children.push(newChild);

              if (newChild.priority > todoItemNode.maxChildrenPriorityNumber)
              todoItemNode.maxChildrenPriorityNumber = newChild.priority;

              getAllChildren(newChild);

            } else {

              getAllChildren(todoItemNode.children.find(itemNode => { return  itemNode.id == childNote.id }));

            }

          });

          todoItemNode.children.sort((a, b) => (a.priority - b.priority));
          // Classifica só aqui depois por prioridade
        }
      }

      getAllChildren(notebookItem);

      this.dataChange.next(this.data);

      //subNote.unsubscribe();

    });

  }

  /** Add an item to to-do list */
 // NOT USED
  insertItem(parent: TodoItemNode, name: string): TodoItemNode {

    if (!parent.children) {
      parent.children = new Array();
      parent.has_child = true;

      if (!parent.parent_id.length && !(parent.subscribeChildren || parent.childrenNotesLoaded))
        this.getAllNoteFromNotebook(parent);
    }

    // atualiza o indice priority de acordo com max do pai
    // incrementa o pai tmb

    let new_note = {
      item: name,
      parent_id: parent.id,
      notebook_id: parent.notebook_id,
      parentRef: parent,
      maxChildrenPriorityNumber: 0,
    } as TodoItemNode;

    parent.children.push(new_note);

    this.dataChange.next(this.data);

    return new_note;
  }

   // not actually used anymore - but let here for reference
   async updateItem(node: TodoItemNode, name: string) {
    node.maxChildrenPriorityNumber = 0;
    node.children = null;

    node.parentRef.maxChildrenPriorityNumber += 100;
    node.priority = node.parentRef.maxChildrenPriorityNumber;

    const notesCollectionRef = collection(
      this.firestore,
      `/users/${this.authService.userId}/notebooks/${node.notebook_id}/notes`
    );

    await addDoc(notesCollectionRef, {
      text: name,
      parent_id: node.parent_id,
      has_child: false,
      priority: node.priority,
      notebook_id: node.notebook_id,
    });

    node.parentRef.children.splice(
      node.parentRef.children.findIndex((itemNode) => itemNode === node),
      1
    );
    this.dataChange.next(this.data);

    if (node.parent_id === node.notebook_id) {
      const notebookDocRef = doc(
        this.firestore,
        `users/${this.authService.userId}/notebooks/${node.parent_id}`
      );

      await updateDoc(notebookDocRef, {
        has_child: true,
        max_children_priority: node.parentRef.maxChildrenPriorityNumber,
      });
    }

    const ipLocation = this.getInsertPointLocation();
    if (ipLocation && node.parent_id === ipLocation.parent_id) {
      const currentLocationDocRef = doc(
        this.firestore,
        `users/${this.authService.userId}/current_location/1`
      );

      await updateDoc(currentLocationDocRef, {
        priority: node.parentRef.maxChildrenPriorityNumber,
      });
    }
  }

}
